import { JRCButton, JRCButtonProps } from 'components/JRCButton/JRCButton';
import { JRCTooltipProps } from '../JRCTooltip/JRCTooltip';
import { Typography } from 'components/JRCTypography/JRCTypography.styles';
import { JRCLoader } from 'components/JRCLoader/JRCLoader';
import React, { useState, useCallback, useRef, MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { PublishButtonEntry, PublishButtonEntryProps } from './PublishButtonEntry';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import { DataCy } from '../../types/dataAttributes';

export type PublishButtonProps = DataCy & {
    sections: Array<{ sectionLabel: string; entries: Array<PublishButtonEntryProps> }>;
    hasFavorites?: boolean;
    loading: boolean;
    label?: string;
    tooltip?: Omit<JRCTooltipProps, 'children'>;
    position?: 'left' | 'right';
    icon: string;
};

const PublishButtonContainer = styled.div`
    position: relative;
    display: inline-block;
    height: 70%;
`;

const PublishButtonDropdown = styled.div<{ position: 'right' | 'left' }>`
    position: absolute; /* Position absolutely within parent */
    top: calc(100%+8px); /* Place directly below the button */
    ${(props) => (props.position === 'left' ? 'right: 0;' : 'left: 0;')}
    background-color: white;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
    z-index: 1;
    border-radius: 8px;
    padding: 8px;
    min-width: 300px;
    max-height: 500px;
    overflow: scroll;
    overflow-x: hidden;
`;

const PublishButtonList = styled.ul`
    list-style: none;
`;

const PublishButton = styled(JRCButton)`
    padding: 9px 16px 8px 16px;
`;

export const JRCPublishButton = ({
    tooltip,
    label,
    icon,
    sections,
    hasFavorites,
    loading,
    position,
    dataCy,
}: PublishButtonProps) => {
    const intl = useIntl();
    const [isPublishOpen, setIsPublishOpen] = useState(false);
    const publishButtonRef = useRef<HTMLButtonElement>(null);

    const clickHandlerWrapper = useCallback((handler: MouseEventHandler<HTMLButtonElement>) => {
        return (e: Parameters<MouseEventHandler<HTMLButtonElement>>[0]) => {
            setIsPublishOpen(false);
            handler(e);
        };
    }, []);

    const toggleIsPublishOpen = useCallback(() => {
        setIsPublishOpen((isOpen) => !isOpen);
    }, []);

    /* Close the menu when the focus leaves it */
    const focusLeave = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsPublishOpen(false);
        }
    }, []);

    /* Focus back to button when escape is pressed */
    const echapPressed = useCallback((e) => {
        if (e.key === 'Escape') {
            setIsPublishOpen(false);
            if (publishButtonRef.current) {
                publishButtonRef.current.focus();
            }
        }
    }, []);

    const buttonProps: Partial<JRCButtonProps> = {
        onClick: toggleIsPublishOpen,
        icon: icon,
    };

    if (tooltip) {
        buttonProps.tooltip = tooltip;
    }
    //9 16 8 16

    return (
        <PublishButtonContainer onBlur={focusLeave} onKeyDown={echapPressed}>
            <PublishButton
                dataCy={dataCy}
                data-testid="publishButton-test"
                ref={publishButtonRef}
                aria-haspopup="true"
                noMargin
                {...buttonProps}>
                {label && <JRCTypography size="l">{label}</JRCTypography>}
            </PublishButton>
            {isPublishOpen && (
                <PublishButtonDropdown
                    data-testid="publishButtonMenu-test"
                    position={position ?? 'left'}
                    data-cy={`${dataCy}-menu`}>
                    {loading ? (
                        <JRCLoader size="m" />
                    ) : (
                        sections.map((section) => {
                            return (
                                <div key={section.sectionLabel}>
                                    <Typography size="l">{intl.formatMessage({ id: section.sectionLabel })}</Typography>
                                    <PublishButtonList>
                                        {section.entries.map((entry) => {
                                            return entry.onClick ? (
                                                <PublishButtonEntry
                                                    hasFavorites={hasFavorites}
                                                    {...entry}
                                                    onClick={clickHandlerWrapper(entry.onClick)}
                                                />
                                            ) : (
                                                <PublishButtonEntry hasFavorites={hasFavorites} {...entry} />
                                            );
                                        })}
                                    </PublishButtonList>
                                </div>
                            );
                        })
                    )}
                </PublishButtonDropdown>
            )}
        </PublishButtonContainer>
    );
};
